import { useState } from 'react'
import { Info, AlertTriangle } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  previewBulkCommitmentLetter,
  type BulkPlanPrepare,
  type BulkSkippedPlan,
  type CommitmentLetterInputs,
  type CommitmentLetterVendor,
} from '@/api/commitmentLetters'
import { LetterPreview } from './LetterPreview'
import { StepTransit } from './StepTransit'
import { vendorLabel } from './utils'

const FILES_PER_VENDOR = 3

interface PlanSelectProps {
  plans: BulkPlanPrepare[]
  value: string
  onChange: (planId: string) => void
  /** Plan ids flagged with a warning icon. */
  flagged?: Set<string>
}

function PlanSelect({ plans, value, onChange, flagged }: PlanSelectProps) {
  return (
    <div className="flex items-center gap-3">
      <Label className="shrink-0">Plan</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 w-[360px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {plans.map(p => (
            <SelectItem key={p.plan_id} value={p.plan_id}>
              {p.plan_name}
              {flagged?.has(p.plan_id) && (
                <AlertTriangle className="h-3.5 w-3.5 text-yellow-600" />
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

interface BulkNoticeProps {
  plans: BulkPlanPrepare[]
  skippedPlans: BulkSkippedPlan[]
}

/** Explains what goes in the ZIP: vendor-bound items only, skipped plans. */
export function BulkNotice({ plans, skippedPlans }: BulkNoticeProps) {
  const withoutVendor = plans.filter(p => p.skipped_without_vendor > 0)

  return (
    <div className="flex flex-col gap-3">
      <Alert variant="info">
        <Info className="h-4 w-4" />
        <AlertTitle>Only line items with a vendor are included</AlertTitle>
        <AlertDescription>
          The ZIP has one folder per plan with the letters for every line item
          of its active version that has a vendor. Line items without a vendor
          are left out.
          {withoutVendor.length > 0 && (
            <ul className="mt-2 list-disc pl-5">
              {withoutVendor.map(p => (
                <li key={p.plan_id}>
                  {p.plan_name}: {p.skipped_without_vendor} line item(s) without
                  vendor
                </li>
              ))}
            </ul>
          )}
        </AlertDescription>
      </Alert>

      {skippedPlans.length > 0 && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>
            {skippedPlans.length} plan(s) will not be in the ZIP
          </AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-5">
              {skippedPlans.map(p => (
                <li key={p.plan_id}>
                  {p.plan_name ?? p.plan_id}: {p.reason}
                </li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}
    </div>
  )
}

interface BulkStepTransitProps {
  plans: BulkPlanPrepare[]
  isRefreshing: boolean
  onStrategyChange: (
    planId: string,
    vendorKey: string,
    code: string | null
  ) => void
  vendorsMissingDates: number
  plansMissingDates: Set<string>
  acknowledgedMissing: boolean
  onAcknowledgeMissing: (value: boolean) => void
}

/** Ports & Transit per plan: pick a plan, then reuse the single-plan step. */
export function BulkStepTransit({
  plans,
  isRefreshing,
  onStrategyChange,
  vendorsMissingDates,
  plansMissingDates,
  acknowledgedMissing,
  onAcknowledgeMissing,
}: BulkStepTransitProps) {
  const [planId, setPlanId] = useState(plans[0]?.plan_id ?? '')
  const plan = plans.find(p => p.plan_id === planId) ?? plans[0]
  if (!plan) return null

  return (
    <div className="flex flex-col gap-4">
      <PlanSelect
        plans={plans}
        value={plan.plan_id}
        onChange={setPlanId}
        flagged={plansMissingDates}
      />
      <StepTransit
        key={plan.plan_id}
        // Items without vendor are excluded in bulk (see the Details notice).
        data={{ ...plan.prepare, line_items_without_vendor: [] }}
        isLoading={false}
        isRefreshing={isRefreshing}
        onStrategyChange={(vendorKey, code) =>
          onStrategyChange(plan.plan_id, vendorKey, code)
        }
        vendorsMissingDates={[]}
        acknowledgedMissing={acknowledgedMissing}
        onAcknowledgeMissing={onAcknowledgeMissing}
      />
      {vendorsMissingDates > 0 && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={acknowledgedMissing}
            onCheckedChange={onAcknowledgeMissing}
          />
          Generate anyway — {vendorsMissingDates} vendor(s) across{' '}
          {plansMissingDates.size} plan(s) will get an empty transit calendar.
        </label>
      )}
    </div>
  )
}

interface BulkStepReviewProps {
  plans: BulkPlanPrepare[]
  inputs: CommitmentLetterInputs
  strategyCodes: Record<string, Record<string, string>>
}

/** Summary of the ZIP plus a letter preview for any plan / vendor. */
export function BulkStepReview({
  plans,
  inputs,
  strategyCodes,
}: BulkStepReviewProps) {
  const [planId, setPlanId] = useState(plans[0]?.plan_id ?? '')
  const plan = plans.find(p => p.plan_id === planId) ?? plans[0]
  const vendors: CommitmentLetterVendor[] = plan?.prepare.vendors ?? []
  const [vendorKey, setVendorKey] = useState(vendors[0]?.vendor_key ?? '')
  const vendor = vendors.find(v => v.vendor_key === vendorKey) ?? vendors[0]
  const totalFiles = plans.reduce(
    (sum, p) => sum + p.prepare.vendors.length * FILES_PER_VENDOR,
    0
  )

  const handlePlanChange = (id: string) => {
    setPlanId(id)
    const next = plans.find(p => p.plan_id === id)
    setVendorKey(next?.prepare.vendors[0]?.vendor_key ?? '')
  }

  if (!plan) return null
  const planCodes = strategyCodes[plan.plan_id] ?? {}

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="max-h-[180px] overflow-auto rounded-md border border-[#d6dae1]">
        <Table>
          <TableHeader className="sticky top-0 bg-white">
            <TableRow>
              <TableHead>Plan</TableHead>
              <TableHead>Vendors</TableHead>
              <TableHead>SKUs</TableHead>
              <TableHead>Files</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plans.map(p => (
              <TableRow key={p.plan_id}>
                <TableCell>{p.plan_name}</TableCell>
                <TableCell>{p.prepare.vendors.length}</TableCell>
                <TableCell>
                  {p.prepare.vendors.reduce(
                    (sum, v) => sum + v.line_item_count,
                    0
                  )}
                </TableCell>
                <TableCell>
                  {p.prepare.vendors.length * FILES_PER_VENDOR}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">
        The ZIP has {plans.length} plan folder(s) and {totalFiles} file(s): the
        letter, commitment units and transit calendar for each vendor.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <PlanSelect
          plans={plans}
          value={plan.plan_id}
          onChange={handlePlanChange}
        />
        <div className="flex items-center gap-3">
          <Label className="shrink-0">Vendor</Label>
          <Select value={vendor?.vendor_key ?? ''} onValueChange={setVendorKey}>
            <SelectTrigger className="h-9 w-[300px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {vendors.map(v => (
                <SelectItem key={v.vendor_key} value={v.vendor_key}>
                  {vendorLabel(v)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <LetterPreview
        queryKey={[
          'commitment-letter-bulk-preview',
          plan.plan_id,
          vendor?.vendor_key,
          planCodes,
          inputs,
        ]}
        queryFn={() =>
          previewBulkCommitmentLetter({
            ...inputs,
            strategy_codes: planCodes,
            plan_id: plan.plan_id,
            vendor_key: vendor!.vendor_key,
          })
        }
        enabled={!!vendor}
      />
    </div>
  )
}
